import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { prepare, layout } from "@chenglou/pretext";

interface ManifestoLine {
  label: string;
  statement: string;
  accent: boolean;
}

const LINES: ManifestoLine[] = [
  { label: "01", statement: "Trust over hype.", accent: true },
  { label: "02", statement: "Useful AI over impressive AI.", accent: false },
  { label: "03", statement: "Clarity before implementation.", accent: true },
];

// Hook: uses pretext to measure exact rendered line height at current container width
function usePretextHeight(
  text: string,
  fontString: string,
  containerWidth: number,
  lineHeightPx: number
): number {
  const [height, setHeight] = useState<number>(lineHeightPx);

  useEffect(() => {
    if (!containerWidth || containerWidth <= 0) return;
    try {
      const prepared = prepare(text, fontString);
      const result = layout(prepared, containerWidth, lineHeightPx);
      setHeight(result.height);
    } catch {
      // Fallback: estimate based on char count
      const charsPerLine = Math.floor(containerWidth / (lineHeightPx * 0.55));
      const lines = Math.ceil(text.length / charsPerLine);
      setHeight(lines * lineHeightPx);
    }
  }, [text, fontString, containerWidth, lineHeightPx]);

  return height;
}

interface ManifestoItemProps {
  line: ManifestoLine;
  index: number;
  containerWidth: number;
}

function ManifestoItem({ line, index, containerWidth }: ManifestoItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "start 0.3"],
  });
  const opacity = useTransform(scrollYProgress, [0, 1], [0.15, 1]);
  const x = useTransform(scrollYProgress, [0, 1], [index % 2 === 0 ? -40 : 40, 0]);
  const scaleX = useTransform(scrollYProgress, [0, 1], [0.88, 1]);

  // Compute font size: clamp(2.4rem, 5.5vw, 5.5rem) - approximate in px for pretext
  const fontSizePx = Math.min(
    Math.max(38.4, containerWidth * 0.055),
    88
  );
  const lineHeightPx = fontSizePx * 1.05;
  // Measure with the actual display font (Fraunces) so pretext's canvas layout
  // matches what renders; a wrong family here skews the reserved line height.
  const fontString = `700 ${fontSizePx}px "Fraunces", serif`;

  // Pretext-measured height for this statement at current container width
  const measuredHeight = usePretextHeight(
    line.statement,
    fontString,
    // subtract label column width (~80px) and gap
    Math.max(containerWidth - 120, 200),
    lineHeightPx
  );

  return (
    <motion.div
      ref={ref}
      style={{ opacity, x, minHeight: measuredHeight + 64 }}
      className="border-b border-ink-3 py-8 lg:py-10 flex items-baseline gap-6 lg:gap-12 group cursor-default"
    >
      <span className="font-mono text-xs text-chalk-3 tracking-widest shrink-0 pt-1 w-8">
        {line.label}
      </span>
      <motion.h2
        style={{ scaleX, transformOrigin: "left center" }}
        className={`font-display font-bold leading-none tracking-tightest text-[clamp(2.4rem,5.5vw,5.5rem)] ${
          line.accent
            ? "text-acid"
            : "text-chalk group-hover:text-chalk-2 transition-colors duration-300"
        }`}
      >
        {line.statement}
      </motion.h2>
    </motion.div>
  );
}

export default function Manifesto() {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.96, 1]);

  // Measure container width and keep it updated on resize
  const measureWidth = useCallback(() => {
    if (innerRef.current) {
      setContainerWidth(innerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    measureWidth();
    const ro = new ResizeObserver(measureWidth);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [measureWidth]);

  return (
    <section id="manifesto" ref={containerRef} className="relative bg-ink/80 py-16 lg:py-36 overflow-hidden">
      {/* Large background text */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden select-none"
        aria-hidden
      >
        <span
          className="font-sans font-bold text-[22vw] leading-none tracking-tightest whitespace-nowrap"
          style={{ color: "transparent", WebkitTextStroke: "1px rgb(var(--manifesto-stroke) / var(--manifesto-stroke-alpha))" }}
        >
          REAL SYSTEMS
        </span>
      </div>

      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16"
        style={{ opacity: bgOpacity }}
        ref={innerRef}
      >
        {/* Header */}
        <motion.div
          className="mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="font-mono text-xs text-acid-soft tracking-widest uppercase">- The Approach</span>
          <h2 className="mt-4 font-display font-bold text-[clamp(2rem,4vw,3.5rem)] text-chalk tracking-tightest leading-none max-w-2xl">
            How we think about this work.
          </h2>
          <p className="mt-5 max-w-xl text-chalk-2 text-base lg:text-lg leading-relaxed font-light">
            Most AI work is performance - impressive demos, vague roadmaps, tools deployed before the problem is understood. A demo that works on Tuesday is not a system that works at 3pm on a Friday. We build for the second one.
          </p>
        </motion.div>

        {/* Lines - pretext-measured */}
        <div className="divide-y divide-ink-3">
          {LINES.map((line, i) => (
            <ManifestoItem
              key={line.label}
              line={line}
              index={i}
              containerWidth={containerWidth}
            />
          ))}
        </div>

        {/* Footnote */}
        <motion.div
          className="mt-16 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p className="font-mono text-sm text-warm max-w-lg leading-relaxed">
            &quot;The goal is not to automate everything. The goal is to build the right leverage at the right place - and know when to stop.&quot;
          </p>
          <button
            onClick={() => {
              const el = document.querySelector("#what-i-do");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="shrink-0 flex items-center gap-2 text-xs font-mono text-acid-soft hover:text-chalk transition-colors duration-200 cursor-pointer whitespace-nowrap group"
          >
            See how we work
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
